import React, { useState } from 'react';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const ScheduleModal = ({ 
  screens, 
  playlists, 
  editingSchedule, 
  onClose, 
  onCreate, 
  onUpdate 
}) => {
  const { t } = useTranslation();
  
  const [formData, setFormData] = useState(
    editingSchedule ? {
      name: editingSchedule.name,
      screen_ids: editingSchedule.screen_ids || [],
      playlist_id: editingSchedule.playlist_id,
      start_time: editingSchedule.start_time.slice(0, 5),
      end_time: editingSchedule.end_time.slice(0, 5),
      days_of_week: editingSchedule.days_of_week || [],
      priority: editingSchedule.priority || 1,
      is_active: editingSchedule.is_active
    } : {
      name: '',
      screen_ids: [],
      playlist_id: '',
      start_time: '09:00',
      end_time: '17:00',
      days_of_week: [],
      priority: 1,
      is_active: true
    }
  );

  const daysOfWeek = [
    { value: 0, label: t('scheduleModal.days.sunday') },
    { value: 1, label: t('scheduleModal.days.monday') },
    { value: 2, label: t('scheduleModal.days.tuesday') },
    { value: 3, label: t('scheduleModal.days.wednesday') },
    { value: 4, label: t('scheduleModal.days.thursday') },
    { value: 5, label: t('scheduleModal.days.friday') },
    { value: 6, label: t('scheduleModal.days.saturday') }
  ];

  const toggleDay = (day) => {
    const newDays = formData.days_of_week.includes(day)
      ? formData.days_of_week.filter(d => d !== day)
      : [...formData.days_of_week, day];
    setFormData({ ...formData, days_of_week: newDays });
  };

  const toggleScreen = (screenId) => {
    const newScreens = formData.screen_ids.includes(screenId)
      ? formData.screen_ids.filter(id => id !== screenId)
      : [...formData.screen_ids, screenId];
    setFormData({ ...formData, screen_ids: newScreens });
  };

  const handleSubmit = () => {
    if (!formData.name || formData.screen_ids.length === 0 || !formData.playlist_id || formData.days_of_week.length === 0) {
      alert(t('scheduleModal.validation.fillAllFields'));
      return;
    }

    if (editingSchedule) {
      onUpdate(editingSchedule.id, formData);
    } else {
      onCreate(formData);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-lg max-w-2xl w-full p-6 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-xl font-bold text-gray-900 dark:text-white">
            {editingSchedule ? t('scheduleModal.title.edit') : t('scheduleModal.title.create')}
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200"
          >
            <X size={24} />
          </button>
        </div>

        <div className="space-y-4">
          {/* Name */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              {t('scheduleModal.fields.name.label')} *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder={t('scheduleModal.fields.name.placeholder')}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              autoComplete="off"
            />
          </div>

          {/* Screens */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              {t('scheduleModal.fields.screens.label')} *
            </label>
            <div className="grid grid-cols-2 gap-2 max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2">
              {screens.map(screen => (
                <label
                  key={screen.id}
                  className={`flex items-center gap-2 p-2 rounded cursor-pointer transition-colors ${
                    formData.screen_ids.includes(screen.id)
                      ? 'bg-blue-100 dark:bg-blue-900'
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={formData.screen_ids.includes(screen.id)}
                    onChange={() => toggleScreen(screen.id)}
                    className="rounded"
                  />
                  <span className="text-sm text-gray-900 dark:text-gray-100">{screen.name}</span>
                </label>
              ))}
            </div>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
              {t('scheduleModal.fields.screens.selected', { count: formData.screen_ids.length })}
            </p>
          </div>

          {/* Playlist */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              {t('scheduleModal.fields.playlist.label')} *
            </label>
            <select
              value={formData.playlist_id}
              onChange={(e) => setFormData({ ...formData, playlist_id: parseInt(e.target.value) })}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            >
              <option value="">{t('scheduleModal.fields.playlist.select')}</option>
              {playlists.map(playlist => (
                <option key={playlist.id} value={playlist.id}>
                  {playlist.name}
                </option>
              ))}
            </select>
          </div>

          {/* Days of Week */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              {t('scheduleModal.fields.daysOfWeek.label')} *
            </label>
            <div className="grid grid-cols-4 gap-2">
              {daysOfWeek.map(day => (
                <button
                  key={day.value}
                  type="button"
                  onClick={() => toggleDay(day.value)}
                  className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    formData.days_of_week.includes(day.value)
                      ? 'bg-primary-600 text-white'
                      : 'bg-gray-200 dark:bg-gray-700 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {day.label.slice(0, 3)}
                </button>
              ))}
            </div>
          </div>

          {/* Time Range */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                {t('scheduleModal.fields.startTime')} *
              </label>
              <input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
                {t('scheduleModal.fields.endTime')} *
              </label>
              <input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
              />
            </div>
          </div>

          {/* Priority */}
          <div>
            <label className="block text-sm font-medium mb-2 text-gray-700 dark:text-gray-300">
              {t('scheduleModal.fields.priority.label')}
            </label>
            <input
              type="number"
              min="1"
              max="10"
              value={formData.priority}
              onChange={(e) => setFormData({ ...formData, priority: parseInt(e.target.value) })}
              className="w-full border border-gray-300 dark:border-gray-600 rounded-lg px-3 py-2 bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
            />
          </div>

          {/* Active Toggle */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="is_active"
              checked={formData.is_active}
              onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
              className="rounded"
            />
            <label htmlFor="is_active" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              {t('scheduleModal.fields.active')}
            </label>
          </div>
        </div>

        {/* Buttons */}
        <div className="flex gap-3 mt-6">
          <button
            onClick={handleSubmit}
            className="flex-1 bg-primary-600 hover:bg-primary-700 text-white px-4 py-2 rounded-lg transition-colors"
          >
            {editingSchedule ? t('scheduleModal.buttons.update') : t('scheduleModal.buttons.create')}
          </button>
          <button
            onClick={onClose}
            className="flex-1 bg-gray-200 hover:bg-gray-300 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-800 dark:text-gray-200 px-4 py-2 rounded-lg transition-colors"
          >
            {t('common.cancel')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ScheduleModal;